import { createRng, hashSeed } from './rng.js';
import { CREW_TALK as CHATTER } from '../data/crewTalk.js';

/**
 * Colleagues talking to each other, not to you.
 *
 * Every few seconds, two people who work here, both stood still or sat within
 * a few feet of each other and neither saying anything already, may swap a
 * line and its answer (`data/crewTalk.js`): said over their heads with
 * `npc.say`, the speaker first and the answer a moment later, and each drawn
 * turned toward the other while they talk. That is all it does. A line over
 * somebody's head is read by nothing but the screen, and turning is the drawn
 * body's (`npcView.js`), so no route, sight cone or timing moves for it.
 *
 * Seeded off the run, the day and the minute, so a seeded run says the same
 * things at the same moments.
 */
export const CHAT = Object.freeze({
  /** Seconds between looks round for two people to talk. */
  every: 3,
  /** How close two people have to be, in metres. */
  reach: 2.8,
  /** The chance a pair that could talk does, at each look. */
  odds: 0.22,
  /** Seconds the answer comes after the line, and each stays up for. */
  answerAfter: 2.2,
  shows: 4,
  /** Game minutes before the same two talk again. */
  rest: 25,
});

export function createCrewTalk({ npcs, seed = () => 'lion', onBreak = () => false }) {
  let since = 0;
  /** Answers waiting to be said: `{ npc, line, in }`. */
  let pending = [];
  /** Who is facing whom, and for how long. */
  const facing = new Map();
  const last = new Map();
  const was = new Map();

  const staff = (npc) => npc.data.department && npc.data.department !== 'guests' && !npc.data.visitor;

  function still(npc) {
    const before = was.get(npc.data.id);
    return before && Math.hypot(before.x - npc.position.x, before.z - npc.position.z) < 0.02;
  }

  function linesFor(a, b) {
    if (onBreak(a.data.id) && onBreak(b.data.id)) return CHATTER.break;
    if (a.data.department === b.data.department && CHATTER[a.data.department]) return [...CHATTER[a.data.department], ...CHATTER.any];
    return CHATTER.any;
  }

  return {
    update(dt, clock) {
      for (const item of pending) item.in -= dt;
      for (const item of pending.filter((p) => p.in <= 0)) {
        if (item.npc.present && !item.npc.note) item.npc.say(`"${item.line}"`, CHAT.shows);
      }
      pending = pending.filter((p) => p.in > 0);
      for (const [id, f] of facing) {
        f.for -= dt;
        if (f.for <= 0) facing.delete(id);
      }

      since += dt;
      if (since < CHAT.every) return;
      since = 0;
      const people = npcs.filter((n) => n.present && staff(n) && !n.aboard && !n.leaving && !n.note && n.suspicion === 0 && still(n) && !facing.has(n.data.id));
      for (const n of npcs) was.set(n.data.id, { x: n.position.x, z: n.position.z });
      const rng = createRng(hashSeed(seed(), 'chat', clock.day, Math.floor(clock.minutes)));
      for (let i = 0; i < people.length; i += 1) {
        for (let j = i + 1; j < people.length; j += 1) {
          const a = people[i];
          const b = people[j];
          if (Math.hypot(a.position.x - b.position.x, a.position.z - b.position.z) > CHAT.reach) continue;
          const key = [a.data.id, b.data.id].sort().join('|');
          const at = clock.day * 1440 + clock.minutes;
          if (last.has(key) && at - last.get(key) < CHAT.rest) continue;
          if (rng.next() >= CHAT.odds) continue;
          const [line, answer] = rng.pick(linesFor(a, b));
          const [first, second] = rng.next() < 0.5 ? [a, b] : [b, a];
          first.say(`"${line}"`, CHAT.shows);
          pending.push({ npc: second, line: answer, in: CHAT.answerAfter });
          const span = CHAT.answerAfter + CHAT.shows;
          facing.set(first.data.id, { at: second.position, for: span });
          facing.set(second.data.id, { at: first.position, for: span });
          last.set(key, at);
          return;
        }
      }
    },

    /** Where somebody is turned to while they talk, or null. */
    facingOf(id) {
      return facing.get(id)?.at ?? null;
    },

    clear() {
      pending = [];
      facing.clear();
      last.clear();
      was.clear();
      since = 0;
    },
  };
}
